/**
 * Tier Progress — Level progression computation for UI display
 *
 * Computes tier-based progress information for the student-facing
 * progress visualization.
 *
 * Extracted from adaptiveEngine.ts in O-2 (god-file split).
 */

import { type ModuleId, type TierConfig, getTierConfig, getTiersForGroup, TIERS, getQuestionsForTier } from '../content';
import { GroupId } from '../config/systemConfig';
import { QuestionState } from './types';
import { V11_CONFIG } from './selection/selectionConfig';
import { getQuestionStateKey } from './questionAttemptHistory';
export interface TierProgressInfo {
  tiers: TierConfig[];
  currentTier: number;
  tierDetails: {
    tier: number;
    label: string;
    title: string;
    isUnlocked: boolean;
    isCurrent: boolean;
    isCompleted: boolean;
    totalQuestions: number;
    attemptedCount: number;
    practicedOrMasteredCount: number;
    progressPercent: number;
    group: number;
  }[];
  /** @deprecated Always `false` since v11. Will be removed in next major. */
  fastTrackActive: boolean;
}

/**
 * Compute tier progress for display.
 * Replaces computeLevelProgress.
 */
export function computeTierProgress(moduleId: ModuleId, group: GroupId, questionStates: Record<string, QuestionState>, startingTier: number): TierProgressInfo {
  const groupTiers = getTiersForGroup(group);
  const effectiveStartingTier = startingTier || groupTiers[0]?.tier || 1;

  // Show tiers for current group + small lookahead
  const currentGroupEnd = groupTiers[groupTiers.length - 1]?.tier || 18;
  const nextGroupStart = group < 8 ? getTiersForGroup(group + 1 as GroupId) : [];
  const lookaheadTiers = new Set(nextGroupStart.slice(0, 2).map(t => t.tier));
  const visibleTiers = TIERS.filter(t => t.tier >= effectiveStartingTier && t.tier <= currentGroupEnd || lookaheadTiers.has(t.tier));

  // Compute unlocked tiers (simplified for v11: based on previous tier completion)
  // In v11, we assume linear progression for the visualization, even if adaptive engine jumps around
  const unlockedTiers = new Set<number>();
  unlockedTiers.add(effectiveStartingTier);

  // Unlock tiers where the user has already practiced questions
  // This handles cases where adaptive engine jumped ahead
  visibleTiers.forEach(tierConfig => {
    const questionsAtTier = getQuestionsForTier(moduleId, tierConfig.tier);
    const hasAttempted = questionsAtTier.some(q => {
      const stateKey = getQuestionStateKey(q.id, group);
      const state = questionStates[stateKey];
      return state && state.attempts > 0;
    });
    if (hasAttempted) {
      unlockedTiers.add(tierConfig.tier);
    }
  });
  const tierDetails = visibleTiers.map(tierConfig => {
    const questionsAtTier = getQuestionsForTier(moduleId, tierConfig.tier);
    let attemptedCount = 0;
    let practicedOrMasteredCount = 0;
    questionsAtTier.forEach(q => {
      const stateKey = getQuestionStateKey(q.id, group);
      const state = questionStates[stateKey];
      if (state && state.attempts > 0) {
        attemptedCount++;
        if (state.status === 'practiced' || state.status === 'mastered') {
          practicedOrMasteredCount++;
        }
      }
    });
    const totalQuestions = questionsAtTier.length;

    // Completion logic
    const isCompleted = totalQuestions > 0 && practicedOrMasteredCount >= Math.ceil(totalQuestions * V11_CONFIG.LEVEL_UNLOCK_THRESHOLD) && attemptedCount >= V11_CONFIG.LEVEL_UNLOCK_MIN_ATTEMPTED;

    // Unlock next tier if this one is completed
    if (isCompleted) {
      unlockedTiers.add(tierConfig.tier + 1);
    }

    // Also unlock if we have significant progress (e.g. started working on it)
    if (attemptedCount > 0) {
      unlockedTiers.add(tierConfig.tier);
    }
    const progressPercent = totalQuestions > 0 ? Math.round(practicedOrMasteredCount / totalQuestions * 100) : 0;
    return {
      tier: tierConfig.tier,
      label: tierConfig.levelCode,
      title: tierConfig.title,
      isUnlocked: false,
      // Set in second pass
      isCurrent: false,
      // Set later
      isCompleted,
      totalQuestions,
      attemptedCount,
      practicedOrMasteredCount,
      progressPercent,
      group: tierConfig.group
    };
  });

  // Second pass to set isUnlocked based on the set we built
  tierDetails.forEach(d => {
    d.isUnlocked = unlockedTiers.has(d.tier);
  });

  // Determine current tier (highest unlocked and not completed)
  let currentTier = effectiveStartingTier;
  for (let i = tierDetails.length - 1; i >= 0; i--) {
    const detail = tierDetails[i];
    if (detail.isUnlocked && !detail.isCompleted && detail.totalQuestions > 0) {
      currentTier = detail.tier;
      break;
    }
  }

  // If all unlocked are completed, move to next
  const allUnlockedCompleted = tierDetails.filter(d => d.isUnlocked && d.totalQuestions > 0).every(d => d.isCompleted);
  if (allUnlockedCompleted) {
    // Find the highest unlocked tier
    let maxUnlocked = 0;
    tierDetails.forEach(d => {
      if (d.isUnlocked) maxUnlocked = Math.max(maxUnlocked, d.tier);
    });
    // If there's a next tier in the list, make it current
    const next = tierDetails.find(d => d.tier === maxUnlocked + 1);
    if (next) currentTier = next.tier;else currentTier = maxUnlocked;
  }
  tierDetails.forEach(d => {
    d.isCurrent = d.tier === currentTier;
  });
  return {
    tiers: visibleTiers,
    currentTier,
    tierDetails,
    fastTrackActive: false // Deprecated in v11
  };
}